import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Heart, ShoppingCart, ArrowLeft, Gift } from 'lucide-react';
import { formatINR } from '../utils/currency';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = (typeof window !== 'undefined' && window.location.origin) || process.env.REACT_APP_BACKEND_URL;

const Donate = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [artTees, setArtTees] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchArtTees = async () => {
      try {
        setLoading(true);
        const { data } = await axios.get(`${BACKEND_URL}/api/products?category=apparel`);
        // Filter only Art Tees
        const tees = data.filter(product => product.name.includes('Art Tee'));
        setArtTees(tees);
      } catch (error) {
        console.error('Error fetching art tees:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchArtTees();
  }, []);

  const addToCart = async (productId) => {
    if (!user) {
      navigate('/login');
      return;
    }

    try {
      await axios.post(
        `${BACKEND_URL}/api/cart/add`,
        { product_id: productId, quantity: 1 },
        { withCredentials: true }
      );
      alert('Added to cart! This donation will help a child in need.');
    } catch (error) {
      console.error('Error adding to cart:', error);
      if (error.response?.status === 401) {
        alert('Session expired. Please login again.');
        navigate('/login');
      } else {
        alert('Failed to add to cart. Please try again.');
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Back Button */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        <button
          onClick={() => navigate('/')}
          className="flex items-center text-purple-600 hover:text-purple-700 mb-6 transition"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </button>
      </div>

      {/* Hero Section - Drishty Foundation Info */}
      <section className="bg-gradient-to-r from-purple-600 to-pink-600 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center mb-6">
            <Heart className="w-16 h-16 fill-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Support Drishty Foundation
          </h1>
          <p className="text-xl md:text-2xl mb-8 opacity-95 leading-relaxed">
            Purchase Art Tees and support education & empowerment of female children.
            23% of every purchase goes directly to Drishty Foundation's programs.
          </p>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 max-w-2xl mx-auto">
            <p className="text-2xl font-bold mb-2">₹2,323 per Art Tee</p>
            <p className="text-xl font-semibold mb-2">₹534 goes to Drishty Foundation (23%)</p>
            <p className="opacity-90">
              Get beautiful artist-designed wearable art delivered to your door while supporting 
              education and empowerment programs for underprivileged female children.
            </p>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">How Your Purchase Makes a Difference</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-purple-600">1</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">Choose Your Art</h3>
              <p className="text-gray-600">
                Browse our collection of Art Tees designed by talented artists from our community
              </p>
            </div>
            <div className="text-center">
              <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-purple-600">2</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">We Deliver to You</h3>
              <p className="text-gray-600">
                Receive your beautiful artist-designed Art Tee at your doorstep
              </p>
            </div>
            <div className="text-center">
              <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-purple-600">3</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">23% Supports Education</h3>
              <p className="text-gray-600">
                ₹534 from your purchase funds education & empowerment programs for female children
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Art Tees Collection */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Art Tees for a Cause</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Select from our Artist Collection. Every purchase supports education and empowerment of female children through Drishty Foundation.
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
            </div>
          ) : artTees.length === 0 ? (
            <div className="text-center py-20">
              <Gift className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 text-lg">
                Art Tees coming soon! Check back later to donate.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
              {artTees.map((product) => (
                <div
                  key={product.id}
                  className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition group"
                >
                  <div
                    className="h-64 bg-gray-200 cursor-pointer relative overflow-hidden"
                    onClick={() => navigate(`/product/${product.id}`)}
                  >
                    {product.images && product.images.length > 0 ? (
                      <img
                        src={product.images[0]}
                        alt={product.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition duration-300"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400">
                        No Image
                      </div>
                    )}
                    {/* Donation Badge */}
                    <div className="absolute top-3 right-3 bg-pink-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
                      23% to Charity
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="text-lg font-semibold mb-2 line-clamp-2">{product.name}</h3>
                    <p className="text-gray-600 text-sm mb-3 line-clamp-2">{product.description}</p>
                    
                    {/* Donation Message */}
                    <div className="bg-purple-50 rounded-lg p-3 mb-3">
                      <div className="flex items-start space-x-2">
                        <Heart className="w-4 h-4 text-pink-500 flex-shrink-0 mt-0.5" />
                        <p className="text-xs text-gray-700">
                          23% (₹534) of this purchase supports education & empowerment of female children through Drishty Foundation
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-2xl font-bold text-purple-600">
                        {formatINR(product.price)}
                      </span>
                      <button
                        onClick={() => addToCart(product.id)}
                        className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition flex items-center space-x-2"
                      >
                        <ShoppingCart className="w-4 h-4" />
                        <span>Add to Cart</span>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* About Drishty Foundation */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-8">About Drishty Foundation</h2>
          <div className="prose prose-lg max-w-none text-gray-700">
            <p className="text-center mb-6">
              Drishty Foundation works with the vision to provide <strong>education, training, employment,
              and eventual self-sustaining empowerment</strong> to the <strong>underprivileged community</strong>,
              with a special focus on female children and women.
            </p>
            <p className="text-center mb-8">
              Through this Art Tee program, we combine the creative talents of artists with
              the power of conscious consumerism to support education and empowerment programs
              for those who need it most.
            </p>
            <div className="bg-purple-50 rounded-lg p-6 text-center">
              <p className="text-gray-800 mb-4">
                <strong>23% of every Art Tee purchase (₹534) goes directly to Drishty Foundation's
                education and empowerment programs for female children.</strong>
              </p>
              <p className="text-gray-600">
                Together, we're building a community that empowers both artists and the next generation
                of empowered women.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 bg-gradient-to-r from-purple-600 to-pink-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Want to Contribute Your Art?</h2>
          <p className="text-lg mb-6 opacity-90">
            Artists can submit their designs to be featured on donation t-shirts
          </p>
          <Link
            to="/submit-art"
            className="inline-block bg-white text-purple-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition"
          >
            Submit Your Art
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Donate;
